import { create } from 'zustand';
import { Client } from 'xrpl';
import * as xummBackend from '../lib/xumm-backend';

// Constants for wallet connection status and types
export const WALLET_STATUS = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error'
};

export const WALLET_TYPE = {
  XAMAN: 'xaman',
  NONE: 'none'
};

/**
 * XRPL Wallet Store - Manages all wallet-related state and functions
 */
const useWallet = create((set, get) => ({
  // State
  status: WALLET_STATUS.DISCONNECTED,
  walletType: WALLET_TYPE.NONE,
  address: null,
  xrpBalance: 0,
  xgcBalance: 0,
  errorMessage: null,
  xummSdk: null,
  xrplClient: null,
  
  // Connection tracking
  currentPayload: null,
  activeSubscription: null,
  connectionTimeout: null,

  // Initialize the wallet store with SDK instances
  initialize: async () => {
    try {
      // Test mode is now strictly controlled by window.ENABLE_XAMAN_TEST_MODE set in src/index.js
      const testMode = window.ENABLE_XAMAN_TEST_MODE === true;

      if (testMode) {
        // This block should ideally not be hit if .env is working and index.js sets testMode to false.
        console.log('!!! Wallet is in TEST MODE. Using mock SDK. !!!');
        // ... (your existing mock SDK setup - keep as is for now)
        set({
          status: WALLET_STATUS.DISCONNECTED, // Start disconnected in test
          errorMessage: null,
          xummSdk: { 
            mocked: true, // Add a flag to identify the mock SDK
            payload: {
              create: async () => ({ 
                uuid: 'test-uuid',
                next: { always: 'https://xumm.app' },
                refs: { qr_png: 'https://raw.githubusercontent.com/XRPL-Labs/XUMM-App/master/assets/icon-white-xumm.png' }
              }),
              subscribe: async (uuid, callback) => {
                setTimeout(() => {
                  const testAccount = 'rPmPErQe4g3TThTJAHQhQabK9jgWHhRZ4D';
                  callback({ data: { signed: true, account: testAccount } });
                  set({
                    status: WALLET_STATUS.CONNECTED,
                    walletType: WALLET_TYPE.XAMAN,
                    address: testAccount,
                    xrpBalance: 100, xgcBalance: 500
                  });
                  console.log('TEST MODE: Wallet connected with test account:', testAccount);
                }, 3000);
                return { unsubscribe: () => {} };
              }
            }
           },
          xrplClient: { 
            isConnected: () => true,
            disconnect: async () => {},
            request: async (req) => {
              if (req.command === 'account_info') return { result: { account_data: { Balance: '100000000' } } };
              if (req.command === 'account_lines') return { result: { lines: [{ currency: 'XGC', account: 'rXGCToken123Issuer', balance: '500' }] } };
              return { result: {} };
            }
           }
        });
        return { success: true, testMode: true };
      }

      // --- REAL INITIALIZATION ---
      const { xrplClient: existingClient } = get();
      
      if (existingClient && existingClient.isConnected()) {
        return { success: true };
      }

      try {
        // Initialize only XRPL client (XUMM SDK is handled by backend)
        let networkUrl = process.env.REACT_APP_XRPL_NETWORK || 'wss://xrplcluster.com';
        console.log('XRPL client connected to:', networkUrl);
        
        const xrplClient = new Client(networkUrl);
        await xrplClient.connect();

        set({ 
          xrplClient,
          status: WALLET_STATUS.DISCONNECTED, // Start disconnected
          errorMessage: null 
        });
        return { success: true };

      } catch (error) {
        console.error('[ERROR useWallet.js] Failed to initialize XRPL Client:', error);
        set({
          status: WALLET_STATUS.ERROR,
          errorMessage: `Error initializing: ${error.message || 'Unknown error'}`
        });
        return { success: false, error };
      }
    } catch (error) {
      console.error('[ERROR useWallet.js] General error in initialize function:', error);
      set({ 
        status: WALLET_STATUS.ERROR,
        errorMessage: error.message || 'Unexpected error during wallet initialization.'
      });
      return { success: false, error };
    }
  },
  
  // Connect to XUMM/Xaman wallet
  connectXaman: async (options = {}) => {
    const { status: currentStatus } = get();
    const { autoOpenPopup = false } = options;
    
    if (window.ENABLE_XAMAN_TEST_MODE === true) {
        console.warn("connectXaman called but in test mode. Using mock connection.");
        set({ status: WALLET_STATUS.CONNECTING });
        setTimeout(() => {
            set({ 
                status: WALLET_STATUS.CONNECTED, 
                address: "test_address_from_mock_sdk", 
                walletType: WALLET_TYPE.XAMAN,
                xrpBalance: 100, 
                xgcBalance: 500 
            });
            console.log("TEST MODE: Mock connection completed.");
        }, 2000);
        return { success: true, qrUrl: "https://raw.githubusercontent.com/XRPL-Labs/XUMM-App/master/assets/icon-white-xumm.png", deepLink: "https://xumm.app", message: "Test mode mock connection."};
    }

    // Clean up any existing connection attempts first
    get().cleanupExistingConnection();

    // Check backend health first
    const backendHealthy = await xummBackend.checkBackendHealth();
    if (!backendHealthy) {
              console.error('Backend server is not available. Please ensure the backend is running on port 5002.');
      set({status: WALLET_STATUS.ERROR, errorMessage: 'Backend server not available. Please start the backend server.'});
      return { success: false, error: new Error('Backend server not available.') };
    }

    if (currentStatus === WALLET_STATUS.CONNECTING) {
        return {success: false, error: {message: "Already connecting."} };
    }

    set({ status: WALLET_STATUS.CONNECTING, errorMessage: null });

    try {
      // Create payload through backend
      const payloadData = await xummBackend.createPayload({
        txjson: { TransactionType: 'SignIn' }
      });

      if (!payloadData.uuid || !payloadData.next?.always || !payloadData.refs?.qr_png) {
        console.error('Invalid payload response from backend:', payloadData);
        throw new Error('Failed to create valid Xaman sign request.');
      }

      // Store payload for tracking
      set({ 
        currentPayload: {
          uuid: payloadData.uuid,
          qrUrl: payloadData.refs.qr_png,
          deepLink: payloadData.next.always
        }
      });

      window.xummSignWindowUrl = payloadData.next.always;
      
      // Only open popup if explicitly requested
      let xamanWindow = null;
      if (autoOpenPopup) {
        xamanWindow = window.open(payloadData.next.always, '_blank', 'width=450,height=700,noopener,noreferrer');
      }
      
      // Subscribe to payload updates through backend
      let isProcessed = false; // Flag to prevent duplicate processing
      
      const subscription = xummBackend.subscribeToPayload(payloadData.uuid, async (event) => {
        // Skip if already processed a successful result
        if (isProcessed) {
          return;
        }
        
        // Handle connection confirmation
        if (event.type === 'connected') {
          console.log('📱 XUMM app connected, awaiting user action...');
          return;
        }
        
        // Only process events with actual data
        if (!event.data) {
          return;
        }
        
        // Handle successful sign-in - primary path
        if (event.data.signed === true && event.data.account) {
          if (isProcessed) return; // Double check
          isProcessed = true;
          
          console.log('✅ Xaman SignIn successful! Account:', event.data.account);
          set({
            status: WALLET_STATUS.CONNECTED,
            walletType: WALLET_TYPE.XAMAN,
            address: event.data.account,
            errorMessage: null,
            // Clear tracking state on success
            currentPayload: null,
            activeSubscription: null,
            connectionTimeout: null
          });
          await get().fetchBalances(event.data.account);
          subscription.unsubscribe();
          if (xamanWindow && !xamanWindow.closed) xamanWindow.close();
          return;
        }
        
        // Handle explicit rejection (signed: false AND resolved: true)
        if (event.data.signed === false && event.data.resolved === true) {
          if (isProcessed) return;
          isProcessed = true;
          
          console.log('❌ Xaman SignIn explicitly rejected (resolved with signed: false).');
          set({ 
            status: WALLET_STATUS.DISCONNECTED, 
            errorMessage: 'Connection rejected by user.',
            // Clear tracking state on rejection
            currentPayload: null,
            activeSubscription: null,
            connectionTimeout: null
          });
          subscription.unsubscribe();
          if (xamanWindow && !xamanWindow.closed) xamanWindow.close();
          return;
        }
        
        // Handle expiration or cancellation
        if (event.data.expired === true || event.data.cancelled === true) {
          if (isProcessed) return;
          isProcessed = true;
          
          console.log('⏰ Xaman payload expired or was cancelled.');
          set({
            status: WALLET_STATUS.ERROR,
            errorMessage: 'Xaman sign request expired or was cancelled.',
            // Clear tracking state on expiry/cancellation
            currentPayload: null,
            activeSubscription: null,
            connectionTimeout: null
          });
          subscription.unsubscribe();
          if (xamanWindow && !xamanWindow.closed) {
            xamanWindow.close();
          }
          return;
        }
        
        // Handle intermediate events or alternative success formats
        if (event.data.signed === false && !event.data.resolved) {
          // Just log status updates, don't process as completion
          console.log('📋 XUMM status update:', event.data);
          
          // Check if there's a rawEvent with different structure (alternative success path)
          if (event.data.rawEvent && !isProcessed) {
            // Try different possible structures in rawEvent
            if (event.data.rawEvent.signed === true || event.data.rawEvent.data?.signed === true) {
              const account = event.data.rawEvent.account || event.data.rawEvent.data?.account;
              if (account) {
                isProcessed = true;
                
                console.log('✅ Found successful sign-in in rawEvent! Account:', account);
                set({
                  status: WALLET_STATUS.CONNECTED,
                  walletType: WALLET_TYPE.XAMAN,
                  address: account,
                  errorMessage: null,
                  // Clear tracking state on success
                  currentPayload: null,
                  activeSubscription: null,
                  connectionTimeout: null
                });
                await get().fetchBalances(account);
                subscription.unsubscribe();
                if (xamanWindow && !xamanWindow.closed) xamanWindow.close();
                return;
              }
            }
          }
          return;
        }
      });

      // Store active subscription for cleanup
      set({ activeSubscription: subscription });
      
      // Enhanced timeout with cleanup
      const timeoutId = setTimeout(() => {
        if (get().status === WALLET_STATUS.CONNECTING) {
          console.log('⏰ Connection timeout reached, cleaning up...');
          subscription.unsubscribe();
          set({
            status: WALLET_STATUS.DISCONNECTED, 
            errorMessage: "Connection timed out. Please try again.",
            // Clear all tracking state on timeout
            currentPayload: null,
            activeSubscription: null,
            connectionTimeout: null
          });
          
          // Close popup if still open
          if (xamanWindow && !xamanWindow.closed) {
            xamanWindow.close();
          }
        }
      }, 180000); // 3 minutes

      // Store timeout ID for cleanup
      set({ connectionTimeout: timeoutId });

      // Return appropriate response based on whether popup was opened
      if (autoOpenPopup && xamanWindow) {
        return {
          success: true,
          popupOpened: true,
          message: "Please complete the sign-in request in the XUMM window."
        };
      } else {
        // Return with QR data for modal
        return {
          success: true,
          qrUrl: payloadData.refs.qr_png,
          deepLink: payloadData.next.always,
          message: "Scan the QR code with your XUMM app to sign in."
        };
      }

    } catch (error) {
      console.error('Error during Xaman connection:', error);
      const errorMessage = error.message || 'Failed to connect to Xaman.';
      set({ status: WALLET_STATUS.ERROR, errorMessage });
      return { success: false, error: { message: errorMessage } };
    }
  },
  
  // Disconnect wallet
  disconnect: () => {
    // Clean up any existing connection attempts first
    get().cleanupExistingConnection();
    
    // Reset the wallet state
    set({
      status: WALLET_STATUS.DISCONNECTED,
      walletType: WALLET_TYPE.NONE,
      address: null,
      xrpBalance: 0,
      xgcBalance: 0,
      errorMessage: null,
      // Clear all tracking state
      currentPayload: null,
      activeSubscription: null,
      connectionTimeout: null
    });
    
    return { success: true };
  },
  
  // Fetch XRP and XGC token balances
  fetchBalances: async (walletAddress) => {
    try {
      const { xrplClient, address: currentAddress } = get();
      const targetAddress = walletAddress || currentAddress;

      if (!xrplClient) {
        throw new Error('XRPL client not initialized');
      }
      if (!targetAddress) {
        throw new Error('No wallet address available');
      }
      
      let xrpBalance = 0;
      let xgcBalance = 0;
      let accountExists = false;

      try {
        const accountInfo = await xrplClient.request({
          command: 'account_info',
          account: targetAddress
        });
        
        xrpBalance = Number(accountInfo.result.account_data.Balance) / 1000000;
        accountExists = true;
      } catch (accountError) {
        // Handle account not found gracefully
        if (accountError.message && accountError.message.includes('Account not found')) {
          console.warn(`Account ${targetAddress} not found on XRPL network. This is normal for new addresses.`);
          xrpBalance = 0;
          accountExists = false;
        } else {
          // Re-throw other errors
          throw accountError;
        }
      }

      // Only try to fetch token balances if account exists
      if (accountExists) {
        const xgcCurrencyCode = process.env.REACT_APP_XGC_CURRENCY || 'XGC';
        const xgcIssuer = process.env.REACT_APP_XGC_ISSUER || 'rXGCToken123Issuer'; 

        try {
          const accountLines = await xrplClient.request({
            command: 'account_lines',
            account: targetAddress,
            ledger_index: 'validated' 
          });
          
          const xgcLine = accountLines.result.lines.find(
            line => line.currency === xgcCurrencyCode && line.account === xgcIssuer
          );
          
          if (xgcLine) xgcBalance = Number(xgcLine.balance);

        } catch (tokenError) {
          console.warn('Could not fetch XGC token balance:', tokenError.message);
          // Don't fail the whole balance fetch if XGC isn't found or errors
        }
      }
        
      set({ xrpBalance, xgcBalance });
      
      // Provide user feedback about account status
      if (!accountExists) {
        set({ 
          errorMessage: null // Clear any previous errors since this is expected for new addresses
        });
        console.log(`New wallet address detected: ${targetAddress}. XRP balance will show once the account is activated with a transaction.`);
      }
      
      return { success: true, xrpBalance, xgcBalance, accountExists };

    } catch (error) {
      console.error('Failed to fetch balances:', error);
      set({errorMessage: `Balance fetch error: ${error.message}`});
      return { success: false, error };
    }
  },
  
  // Send XGC tokens (basic example, needs XUMM SDK for signing)
  sendXgc: async (recipient, amount) => {
    const { xummSdk, address, xrplClient } = get();
    if (!xummSdk || !address || !xrplClient) {
      console.error("Cannot send XGC: Wallet not fully connected or SDK not ready.");
      return { success: false, error: { message: "Wallet not ready." } };
    }

    const xgcCurrencyCode = process.env.REACT_APP_XGC_CURRENCY || 'XGC';
    const xgcIssuer = process.env.REACT_APP_XGC_ISSUER || 'rXGCToken123Issuer';

    try {
      const tx = {
        TransactionType: 'Payment',
        Account: address,
        Destination: recipient,
        Amount: {
          currency: xgcCurrencyCode,
          issuer: xgcIssuer,
          value: amount.toString()
        }
      };

      // Create payload with XUMM SDK
      const payload = await xummSdk.payload.create({ txjson: tx });
      window.open(payload.next.always, '_blank', 'width=450,height=700,noopener,noreferrer');

      // Subscribe to this payload
      const subscription = await xummSdk.payload.subscribe(payload.uuid, async event => {
        if (event.data.signed === true) {
          console.log('XGC Payment signed!', event.data);
          // Optionally verify transaction on ledger here before confirming to user
          set({ status: get().status }); // Re-trigger effects, maybe update balances
          get().fetchBalances(); // Refresh balances
          subscription.unsubscribe();
        } else if (event.data.signed === false) {
          console.log('XGC Payment rejected.');
          set({ errorMessage: "XGC payment rejected by user." });
          subscription.unsubscribe();
        }
      });

      return { success: true, uuid: payload.uuid, qrUrl: payload.refs.qr_png, deepLink: payload.next.always };

    } catch (error) {
      console.error('Failed to send XGC:', error);
      const errorMessage = error.error?.message || error.message || 'Failed to send XGC.';
      set({ errorMessage });
      return { success: false, error: { message: errorMessage } };
    }
  },
  
  // Clean up resources when app unmounts
  cleanup: async () => {
    const { xrplClient } = get();
    if (xrplClient && xrplClient.isConnected()) {
      await xrplClient.disconnect();
      console.log("XRPL Client disconnected during cleanup.");
    }
    return { success: true };
  },

  // Clean up existing connection attempts
  cleanupExistingConnection: () => {
    const state = get();
    console.log('Cleaning up existing connection attempts...');
    
    // Clear any timeouts
    if (state.connectionTimeout) {
      console.log('Clearing connection timeout');
      clearTimeout(state.connectionTimeout);
      set({ connectionTimeout: null });
    }
    
    // Unsubscribe from any active subscription
    if (state.activeSubscription) {
      console.log('Unsubscribing from existing payload subscription');
      try {
        state.activeSubscription.unsubscribe();
      } catch (error) {
        console.warn('Error unsubscribing:', error);
      }
      set({ activeSubscription: null });
    }
    
    // Clear current payload
    if (state.currentPayload) {
      console.log('Clearing existing payload:', state.currentPayload.uuid);
      set({ currentPayload: null });
    }
    
    // Reset status if connecting
    if (state.status === WALLET_STATUS.CONNECTING) {
      console.log('Resetting connection status');
      set({ 
        status: WALLET_STATUS.DISCONNECTED,
        errorMessage: null
      });
    }
  },

  // Cancel ongoing connection attempt
  cancelConnection: async () => {
    const state = get();
    console.log('Cancelling ongoing connection attempt...');
    
    try {
      // Clean up existing connection state first
      get().cleanupExistingConnection();
      
      // Try to cancel the payload on the backend if we have a UUID
      if (state.currentPayload?.uuid) {
        try {
          console.log('Attempting to cancel payload on backend:', state.currentPayload.uuid);
          const success = await xummBackend.cancelPayload(state.currentPayload.uuid);
          
          if (success) {
            console.log('Successfully cancelled payload on backend');
          } else {
            console.warn('Failed to cancel payload on backend');
          }
        } catch (error) {
          console.warn('Error cancelling payload on backend:', error.message);
        }
      }
      
      // Final state reset
      set({ 
        status: WALLET_STATUS.DISCONNECTED,
        errorMessage: null,
        currentPayload: null,
        activeSubscription: null,
        connectionTimeout: null
      });
      
      return { success: true };
      
    } catch (error) {
      console.error('Error cancelling connection:', error);
      return { success: false, error: error.message };
    }
  },
}));

export default useWallet;